/** What is stored, per table. Container load is Azure Monitor's, not ours. */

import { useEffect, useState } from 'react'
import {
  Caption1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  EmptyState,
  LoadingState,
  MonoText,
  SectionCard,
  formatTimestamp,
} from '../platform'
import { api } from '../api'
import type { StorageReport } from '../api'
import { holdsFrom, readableCount, readableSize } from './storageReading'

const useStyles = makeStyles({
  // Five columns do not fit a phone. Below this width the table scrolls
  // sideways inside its card; without it the columns print over each other.
  scroll: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    minWidth: '640px',
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  note: {
    color: tokens.colorNeutralForeground3,
    display: 'block',
    marginTop: tokens.spacingVerticalS,
  },
})

export default function StoragePanel() {
  const styles = useStyles()
  const [report, setReport] = useState<StorageReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<StorageReport>('/storage')
      .then(setReport)
      .catch((e) => setError(e.message))
  }, [])

  return (
    <SectionCard
      title="What is stored"
      description="Per table, largest first. Container CPU, memory and disk are not here on purpose: those are platform metrics and live in Grafana, while what is on this page is the part that says something about the service."
    >
      {error ? (
        <EmptyState title="The sizes cannot be read" description={error} />
      ) : !report ? (
        <LoadingState label="Measuring" />
      ) : (
        <>
          <div className={styles.scroll}>
          <Table size="small" className={styles.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Table</TableHeaderCell>
                <TableHeaderCell>Rows</TableHeaderCell>
                <TableHeaderCell>Size</TableHeaderCell>
                <TableHeaderCell>Holds from</TableHeaderCell>
                <TableHeaderCell>Counted</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* `?? []` BECAUSE THIS PANEL TAKES THE WHOLE PAGE DOWN.
                  A throw here unmounts Settings, and the failure then reads as
                  "the button is gone" or "the lane card is missing", which
                  sends whoever is debugging to the thing they just changed.
                  That has now happened twice: once when a new field made the
                  page re-fetch, and once when another test's double answered
                  this path with a different shape.
                  An answer without `tables` is a bad answer and worth seeing
                  as an empty table, not worth losing the page over. */}
              {(report.tables ?? []).map((table) => (
                <TableRow key={table.name}>
                  <TableCell>
                    <MonoText>{table.name}</MonoText>
                  </TableCell>
                  <TableCell className={styles.nowrap}>{readableCount(table)}</TableCell>
                  <TableCell className={styles.nowrap}>
                    {readableSize(table.bytes)}
                  </TableCell>
                  <TableCell className={styles.nowrap}>{holdsFrom(table)}</TableCell>
                  <TableCell className={styles.nowrap}>
                    {table.counted_at ? formatTimestamp(table.counted_at) : 'never'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          {/* The caveat comes from the service, so it cannot go on being shown
              after it stops being true. Both halves are said: what the number
              is, and what one reading cannot tell you. */}
          <Caption1 className={styles.note}>
            {`Total ${readableSize(report.total_bytes)}. Row counts are ${report.counts_are} by the query planner, not counted row by row: counting each table would get slower exactly as the thing it measures grows. One reading says how much is stored and nothing about how fast, so growth is a question for Grafana. "Holds from" is the oldest row still in the table, which is what a retention window would be measured against; how the rows are spread across that span is a sequential scan in every form, so it is a maintenance query rather than something this page runs.`}
          </Caption1>
        </>
      )}
    </SectionCard>
  )
}
